import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { withStyles } from '@mui/styles';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMore from '@mui/icons-material/ExpandMore';

const SUPPORT_EMAIL = 'mta-support@hearst.com';

const styles = (theme) => ({
  page: {
    maxWidth: 640,
    margin: '40px auto',
    padding: 32
  },
  banner: {
    margin: '16px 20px 0'
  },
  message: {
    marginTop: 4
  },
  hint: {
    marginTop: 8
  },
  accordion: {
    marginTop: 16,
    boxShadow: 'none',
    backgroundColor: 'transparent',
    '&:before': {
      display: 'none'
    }
  },
  accordionSummary: {
    padding: 0,
    minHeight: 36,
    '& .MuiAccordionSummary-content': {
      margin: '8px 0'
    }
  },
  techRow: {
    marginTop: 4,
    wordBreak: 'break-word'
  },
  details: {
    marginTop: 8,
    paddingLeft: 20
  },
  actions: {
    marginTop: 24
  },
  support: {
    marginTop: 16,
    color: theme.palette.text.secondary
  }
});

class ApiErrorView extends Component {
  reload = () => {
    window.location.reload();
  };

  renderTechnicalDetails() {
    const { classes, parsed } = this.props;
    const details = parsed.details || [];
    const statusLabel = parsed.status ? `${parsed.status}` : 'Unknown';
    const hasTech = parsed.sourceLabel || parsed.rawMessage || details.length > 0;

    if (!hasTech) {
      return null;
    }

    return (
      <Accordion className={classes.accordion} disableGutters>
        <AccordionSummary
          expandIcon={<ExpandMore />}
          className={classes.accordionSummary}
        >
          <Typography variant="caption">Technical details</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="caption" component="p" className={classes.techRow}>
            Source: {parsed.sourceLabel}
          </Typography>
          <Typography variant="caption" component="p" className={classes.techRow}>
            HTTP status: {statusLabel}
          </Typography>
          {parsed.rawMessage && (
            <Typography variant="caption" component="p" className={classes.techRow}>
              Server message: {parsed.rawMessage}
            </Typography>
          )}
          {details.length > 0 && (
            <ul className={classes.details}>
              {details.map((detail, index) => (
                <li key={`${index}-${detail}`}>
                  <Typography variant="caption">{detail}</Typography>
                </li>
              ))}
            </ul>
          )}
        </AccordionDetails>
      </Accordion>
    );
  }

  render() {
    const { classes, parsed, variant } = this.props;
    if (!parsed || !parsed.hasError) {
      return null;
    }

    const severity = parsed.source === 'network' ? 'warning' : 'error';

    const body = (
      <React.Fragment>
        <AlertTitle>{parsed.title}</AlertTitle>
        <Typography variant="body2" component="p" className={classes.message}>
          {parsed.message}
        </Typography>
        {parsed.hint && (
          <Typography variant="body2" component="p" className={classes.hint}>
            {parsed.hint}
          </Typography>
        )}
        {this.renderTechnicalDetails()}
        {variant === 'page' && (
          <React.Fragment>
            <div className={classes.actions}>
              <Button variant="contained" color="primary" onClick={this.reload}>
                Reload page
              </Button>
            </div>
            <Typography variant="body2" className={classes.support} component="p">
              If this keeps happening, email {SUPPORT_EMAIL} with this page URL and what you were doing.
            </Typography>
          </React.Fragment>
        )}
      </React.Fragment>
    );

    if (variant === 'banner') {
      return (
        <Alert severity={severity} className={classes.banner}>
          {body}
        </Alert>
      );
    }

    return (
      <Paper className={classes.page} elevation={1}>
        <Alert severity={severity} variant="outlined">
          {body}
        </Alert>
      </Paper>
    );
  }
}

ApiErrorView.defaultProps = {
  entity: '',
  variant: 'page'
};

ApiErrorView.propTypes = {
  classes: PropTypes.object.isRequired,
  parsed: PropTypes.object.isRequired,
  entity: PropTypes.string,
  variant: PropTypes.oneOf(['page', 'banner'])
};

export default withStyles(styles)(ApiErrorView);
